import React, { useEffect, useState } from 'react';
import { healthAPI } from '../api';
import { LogEntry } from '../types';
import { RefreshCcw, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import './OperationalDashboard.css';

interface DayData {
    date: string;
    count: number;
}

const ActivityHeatmap: React.FC = () => {
    const [dayData, setDayData] = useState<Map<string, number>>(new Map());
    const [loading, setLoading] = useState(true);
    const [totalEvents, setTotalEvents] = useState(0);
    const [currentStreak, setCurrentStreak] = useState(0);
    const [longestStreak, setLongestStreak] = useState(0);

    useEffect(() => {
        fetchActivity();
    }, []);

    const fetchActivity = async () => {
        setLoading(true);
        try {
            // Fetch last 6 months of events
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 180);

            const data = await healthAPI.getEvents({
                start: start.toISOString(),
                end: end.toISOString(),
                limit: 5000,
            });

            // Count events per day
            const counts = new Map<string, number>();
            for (const event of data.events) {
                const date = new Date(event.timestamp).toISOString().split('T')[0];
                counts.set(date, (counts.get(date) || 0) + 1);
            }
            setDayData(counts);
            setTotalEvents(data.events.length);

            // Calculate streaks
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            let streak = 0;
            let maxStreak = 0;
            let tempStreak = 0;
            
            // Current streak (counting back from today)
            for (let i = 0; i <= 180; i++) {
                const d = new Date(today);
                d.setDate(d.getDate() - i);
                const key = d.toISOString().split('T')[0];
                if (counts.has(key)) {
                    if (i === 0 || streak === i) streak++;
                } else if (i > 0 && streak < i) {
                    break;
                }
            }
            setCurrentStreak(streak);

            // Longest streak
            const sortedDates = Array.from(counts.keys()).sort();
            tempStreak = 1;
            maxStreak = 1;
            for (let i = 1; i < sortedDates.length; i++) {
                const prev = new Date(sortedDates[i - 1]);
                const curr = new Date(sortedDates[i]);
                const diff = (curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24);
                if (diff === 1) {
                    tempStreak++;
                    maxStreak = Math.max(maxStreak, tempStreak);
                } else {
                    tempStreak = 1;
                }
            }
            setLongestStreak(maxStreak);
        } catch (err) {
            console.error('Failed to fetch activity:', err);
        } finally {
            setLoading(false);
        }
    };

    const getIntensity = (count: number): number => {
        if (count === 0) return 0;
        if (count <= 2) return 1;
        if (count <= 5) return 2;
        if (count <= 10) return 3;
        return 4;
    };

    const renderHeatmap = () => {
        const weeks: Array<Array<{ date: string; count: number; dayOfWeek: number }>> = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Start from 26 weeks ago, aligned to Sunday
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - (26 * 7) - startDate.getDay());

        let currentWeek: Array<{ date: string; count: number; dayOfWeek: number }> = [];
        const cursor = new Date(startDate);

        while (cursor <= today) {
            const dateStr = cursor.toISOString().split('T')[0];
            const dayOfWeek = cursor.getDay();

            currentWeek.push({
                date: dateStr,
                count: dayData.get(dateStr) || 0,
                dayOfWeek,
            });

            if (dayOfWeek === 6) {
                weeks.push(currentWeek);
                currentWeek = [];
            }
            cursor.setDate(cursor.getDate() + 1);
        }
        if (currentWeek.length > 0) {
            weeks.push(currentWeek);
        }

        // Month labels
        const monthLabels: Array<{ label: string; weekIndex: number }> = [];
        let lastMonth = -1;
        weeks.forEach((week, i) => {
            const firstDay = new Date(week[0].date);
            const month = firstDay.getMonth();
            if (month !== lastMonth) {
                monthLabels.push({
                    label: firstDay.toLocaleString('default', { month: 'short' }),
                    weekIndex: i,
                });
                lastMonth = month;
            }
        });

        return (
            <div className="heatmap-wrapper">
                <div className="heatmap-months">
                    {monthLabels.map((m, i) => (
                        <span
                            key={i}
                            className="month-label"
                            style={{ gridColumnStart: m.weekIndex + 2 }}
                        >
                            {m.label}
                        </span>
                    ))}
                </div>
                <div className="heatmap-grid">
                    <div className="heatmap-days">
                        <span></span>
                        <span>Mon</span>
                        <span></span>
                        <span>Wed</span>
                        <span></span>
                        <span>Fri</span>
                        <span></span>
                    </div>
                    <div className="heatmap-cells">
                        {weeks.map((week, wi) => (
                            <div key={wi} className="heatmap-week">
                                {week.map((day, di) => (
                                    <div
                                        key={di}
                                        className={`heatmap-cell intensity-${getIntensity(day.count)}`}
                                        title={`${day.date}: ${day.count} events`}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="heatmap-legend">
                    <span className="legend-label">Less</span>
                    <div className="heatmap-cell intensity-0" />
                    <div className="heatmap-cell intensity-1" />
                    <div className="heatmap-cell intensity-2" />
                    <div className="heatmap-cell intensity-3" />
                    <div className="heatmap-cell intensity-4" />
                    <span className="legend-label">More</span>
                </div>
            </div>
        );
    };

    if (loading) {
        return <div className="heatmap-section"><div className="loading-state">Loading activity...</div></div>;
    }

    return (
        <div className="heatmap-section">
            <div className="heatmap-stats">
                <div className="heatmap-stat">
                    <span className="stat-value">{totalEvents}</span>
                    <span className="stat-label">events (6mo)</span>
                </div>
                <div className="heatmap-stat">
                    <span className="stat-value">{currentStreak}</span>
                    <span className="stat-label">day streak</span>
                </div>
                <div className="heatmap-stat">
                    <span className="stat-value">{longestStreak}</span>
                    <span className="stat-label">longest streak</span>
                </div>
                <div className="heatmap-stat">
                    <span className="stat-value">{dayData.size}</span>
                    <span className="stat-label">active days</span>
                </div>
            </div>
            {renderHeatmap()}
        </div>
    );
};

export const OperationalDashboard: React.FC = () => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showErrorsOnly, setShowErrorsOnly] = useState(false);

    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await healthAPI.getOperationalData();
            setLogs(data.logs);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    const getLogIcon = (message: string) => {
        if (message.includes('ERROR') || message.includes('Exception')) return <AlertTriangle className="error-icon" />;
        if (message.includes('WARNING')) return <AlertCircle className="warning-icon" />;
        return <Info className="info-icon" />;
    };

    const formatTime = (timestamp: number) => {
        return new Date(timestamp).toLocaleString();
    };

    const filteredLogs = showErrorsOnly
        ? logs.filter(log => log.message.includes('ERROR') || log.message.includes('Exception') || log.message.includes('AccessDenied'))
        : logs;

    return (
        <div className="operational-dashboard">
            <div className="dashboard-header">
                <div className="header-controls">
                    <button
                        className={`filter-button ${showErrorsOnly ? 'active' : ''}`}
                        onClick={() => setShowErrorsOnly(!showErrorsOnly)}
                    >
                        <AlertTriangle size={18} />
                        {showErrorsOnly ? 'Errors Only' : 'All Logs'}
                    </button>
                    <button onClick={fetchLogs} disabled={loading} className="refresh-button">
                        <RefreshCcw size={18} className={loading ? 'spinning' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            {error && <div className="error-banner">{error}</div>}

            {/* Activity Heatmap */}
            <ActivityHeatmap />

            <div className="logs-container">
                <h3>Recent System Logs</h3>
                {loading && logs.length === 0 ? (
                    <div className="loading-state">Loading logs...</div>
                ) : logs.length === 0 ? (
                    <div className="empty-state">No relevant logs found in the last 7 days.</div>
                ) : filteredLogs.length === 0 ? (
                    <div className="empty-state">No error logs found.</div>
                ) : (
                    <div className="logs-list">
                        {filteredLogs.map((log, index) => (
                            <div key={index} className="log-entry">
                                <div className="log-meta">
                                    <span className="log-time">{formatTime(log.timestamp)}</span>
                                    <span className="log-group">{log.logGroup.split('/').pop()}</span>
                                </div>
                                <div className="log-content">
                                    {getLogIcon(log.message)}
                                    <pre className="log-message">{log.message}</pre>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
